import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Announcement } from '../types/Announcement';
import { formatAnnouncementDate, prepareAnnouncementHtml, validAnnouncementRumuz } from '../utils/announcement';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import AnimatedCloseButton from './AnimatedCloseButton';
import AnnouncementHtml from './AnnouncementHtml';

type AnnouncementDetailModalProps = {
  announcement: Announcement | null;
  isRead: boolean;
  canMarkAll: boolean;
  marking: boolean;
  markError: boolean;
  onClose: () => void;
  onConfirmRead: (id: number, markAll: boolean) => void;
};

export default function AnnouncementDetailModal({
  announcement,
  isRead,
  canMarkAll,
  marking,
  markError,
  onClose,
  onConfirmRead,
}: AnnouncementDetailModalProps) {
  const [markAll, setMarkAll] = useState(false);
  const isOpen = announcement != null;
  useScrollPrevention(isOpen);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useModalBackHandler({
    isOpen,
    onClose: handleClose,
  });

  useEffect(() => {
    setMarkAll(false);
  }, [announcement?.Id, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const body = announcement ? prepareAnnouncementHtml(announcement.Duyuru) : '';
  const hasFile = announcement != null && validAnnouncementRumuz(announcement.DosyaRumuz) != null;

  return (
    <AnimatePresence>
      {announcement && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 overflow-y-auto bg-black/20 dark:bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="announcement-detail-title"
        >
          <div className="flex min-h-full items-center justify-center px-2 py-4 sm:p-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', bounce: 0.25, duration: 0.4 }}
              className="relative w-full mx-2 sm:mx-0 sm:my-8 sm:max-w-lg overflow-hidden rounded-xl border border-white/50 dark:border-gray-600/50 bg-gradient-to-br from-white/95 via-white/92 to-white/90 dark:from-gray-700/95 dark:via-gray-750/93 dark:to-gray-800/95 text-left shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatAnnouncementDate(announcement.Tarih)}
                  </p>
                  <AnimatedCloseButton
                    onClick={onClose}
                    size="sm"
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-all duration-200"
                  />
                </div>

                <h2 id="announcement-detail-title" className="text-xl font-semibold text-gray-900 dark:text-white">
                  {announcement.Baslik}
                </h2>

                {(body || hasFile) && (
                  <div className="mt-4 max-h-[50vh] overflow-y-auto pr-1">
                    <AnnouncementHtml
                      html={announcement.Duyuru}
                      servisBulteniMi={announcement.ServisBulteniMi}
                      dosyaRumuz={announcement.DosyaRumuz}
                    />
                  </div>
                )}

                {!isRead && announcement.Id != null && (
                  <div className="mt-6">
                    {canMarkAll && (
                      <label className="mb-4 flex items-start gap-3 cursor-pointer border-t border-gray-200 pt-4 text-sm text-gray-700 dark:text-gray-200 dark:border-gray-600">
                        <input
                          type="checkbox"
                          checked={markAll}
                          onChange={(event) => setMarkAll(event.target.checked)}
                          disabled={marking}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>Tüm duyuruları okundu olarak işaretle</span>
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => onConfirmRead(announcement.Id as number, canMarkAll && markAll)}
                      disabled={marking}
                      className="rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:from-blue-600 hover:to-indigo-700 disabled:opacity-60"
                    >
                      {marking ? 'İşaretleniyor...' : 'Okudum'}
                    </button>
                    {markError && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                        Okundu işaretlenemedi. Tekrar deneyin.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
